/**
 * TanStack Query hooks over the `admin.agentContext.*` oRPC routes. `tenantId`
 * is a string for T2 and `null` for T1. Every mutation invalidates the matching
 * list query and the per-row revisions query, so the editor refreshes without a
 * manual reload.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "../../api";
import type {
  AgentFieldDefinition,
  AgentFieldDefinitionRevision,
  AgentSkill,
  AgentSkillRevision,
  AgentTableDefinition,
  AgentTableDefinitionRevision,
  AgentTenantConfig,
  AgentTenantConfigRevision,
  FieldBody,
  MutationResult,
  PortalTenant,
  WarehouseSchema,
} from "./types";

// ─── Query keys ──────────────────────────────────────────────────────────────

export const qk = {
  tenants: ["agent-context", "tenants"] as const,
  tenantsWithFlags: ["agent-context", "tenants-with-flags"] as const,
  skills: (tenantId: string | null) => ["agent-context", "skills", tenantId] as const,
  skillRevisions: (id: string) => ["agent-context", "skills", "revisions", id] as const,
  tableDefs: (tenantId: string | null) => ["agent-context", "table-defs", tenantId] as const,
  tableDefRevisions: (id: string) => ["agent-context", "table-defs", "revisions", id] as const,
  fieldDefs: (tenantId: string | null) => ["agent-context", "field-defs", tenantId] as const,
  fieldDefRevisions: (id: string) => ["agent-context", "field-defs", "revisions", id] as const,
  tenantConfig: (tenantId: string) => ["agent-context", "tenant-config", tenantId] as const,
  tenantConfigRevisions: (tenantId: string) =>
    ["agent-context", "tenant-config", "revisions", tenantId] as const,
  warehouseSchema: (tenantId: string | null) =>
    ["agent-context", "warehouse-schema", tenantId] as const,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function encodeTenantId(tenantId: string | null): string {
  // oRPC's OpenAPIHandler routes the `tenantId` query parameter. Encode `null`
  // as the literal string "null" — that's the on-wire convention used by the
  // backend handler (`where: { tenantId: input.tenantId }` with a Zod-parsed
  // nullable string).
  return tenantId === null ? "null" : encodeURIComponent(tenantId);
}

function listQS(tenantId: string | null): string {
  return `?tenantId=${encodeTenantId(tenantId)}`;
}

// ─── Tenants ─────────────────────────────────────────────────────────────────

export function useTenants(): UseQueryResult<PortalTenant[]> {
  // Reuse the admin feature-flags tenants list — the only existing admin route
  // that returns every PortalTenant for super-admin browsing.
  return useQuery({
    queryKey: qk.tenants,
    queryFn: () => apiFetch<PortalTenant[]>("/api/feature-flags/tenants"),
  });
}

/**
 * Tenants enriched with `ai_agent.dottie` flag state and `slug`, so the context
 * picker shows at a glance which tenants actually have Dottie on — rather than
 * authoring context for one where the agent is disabled.
 */
export interface PortalTenantWithDottieState {
  id: string;
  name: string;
  slug: string;
  dottieEnabled: boolean;
}

export function useTenantsWithDottieState(): UseQueryResult<PortalTenantWithDottieState[]> {
  return useQuery({
    queryKey: qk.tenantsWithFlags,
    queryFn: () => apiFetch<PortalTenantWithDottieState[]>("/api/admin/dottie-tenants"),
  });
}

// ─── Skills ──────────────────────────────────────────────────────────────────

export function useSkills(tenantId: string | null): UseQueryResult<AgentSkill[]> {
  return useQuery({
    queryKey: qk.skills(tenantId),
    queryFn: () => apiFetch<AgentSkill[]>(`/api/admin/agent-context/skills${listQS(tenantId)}`),
  });
}

export interface CreateSkillInput {
  tenantId: string | null;
  name: string;
  description: string;
  body: string;
  reason?: string;
}

export function useCreateSkill(
  tenantId: string | null,
): UseMutationResult<MutationResult<AgentSkill>, Error, CreateSkillInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiFetch<MutationResult<AgentSkill>>("/api/admin/agent-context/skills", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.skills(tenantId) });
    },
  });
}

export interface UpdateSkillInput {
  id: string;
  name?: string;
  description?: string;
  body?: string;
  reason?: string;
}

export function useUpdateSkill(
  tenantId: string | null,
): UseMutationResult<MutationResult<AgentSkill>, Error, UpdateSkillInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...rest }) =>
      apiFetch<MutationResult<AgentSkill>>(
        `/api/admin/agent-context/skills/${encodeURIComponent(id)}`,
        { method: "PATCH", body: JSON.stringify(rest) },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.skills(tenantId) });
      void qc.invalidateQueries({ queryKey: qk.skillRevisions(vars.id) });
    },
  });
}

export interface DeleteEntityInput {
  id: string;
  reason?: string;
}

export function useDeleteSkill(
  tenantId: string | null,
): UseMutationResult<void, Error, DeleteEntityInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }) =>
      apiFetch<void>(`/api/admin/agent-context/skills/${encodeURIComponent(id)}`, {
        method: "DELETE",
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.skills(tenantId) });
    },
  });
}

// ─── Atomic batch save (SIM-1724) ────────────────────────────────────────────

/**
 * Mirrors `contextSave.contract.ts` (`POST /admin/agent-context/save`). Only the
 * `skills` slice is sent; the other section deltas default to empty
 * server-side, so they are optional here.
 */
interface SkillsDelta {
  creates: Array<{ name: string; description: string; body: string }>;
  updates: Array<{ id: string; name: string; description: string; body: string }>;
  deletes: string[];
}

export interface SaveAgentContextInput {
  tenantId: string;
  reason: string;
  skills: SkillsDelta;
}

interface SectionCounts {
  created: number;
  updated: number;
  deleted: number;
}

export interface SaveAgentContextResult {
  applied: {
    skills: SectionCounts;
    tableDefs: SectionCounts;
    fieldDefs: SectionCounts;
    additionalContext: { updated: boolean };
  };
  warnings: string[];
}

export function useSaveAgentContext(
  tenantId: string,
): UseMutationResult<SaveAgentContextResult, Error, SaveAgentContextInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiFetch<SaveAgentContextResult>("/api/admin/agent-context/save", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.skills(tenantId) });
      // Revisions are keyed per-row; the batch can touch many. Invalidate the
      // whole skills subtree (list + every per-row revisions cache).
      void qc.invalidateQueries({ queryKey: ["agent-context", "skills"] });
    },
  });
}

export function useSkillRevisions(id: string | null): UseQueryResult<AgentSkillRevision[]> {
  return useQuery({
    queryKey: qk.skillRevisions(id ?? ""),
    queryFn: () =>
      apiFetch<AgentSkillRevision[]>(
        `/api/admin/agent-context/skills/${encodeURIComponent(id ?? "")}/revisions`,
      ),
    enabled: !!id,
  });
}

// ─── Table definitions ───────────────────────────────────────────────────────

export function useTableDefs(tenantId: string | null): UseQueryResult<AgentTableDefinition[]> {
  return useQuery({
    queryKey: qk.tableDefs(tenantId),
    queryFn: () =>
      apiFetch<AgentTableDefinition[]>(`/api/admin/agent-context/table-defs${listQS(tenantId)}`),
  });
}

export interface CreateTableDefInput {
  tenantId: string | null;
  tableName: string;
  description: string;
  reason?: string;
}

export function useCreateTableDef(
  tenantId: string | null,
): UseMutationResult<MutationResult<AgentTableDefinition>, Error, CreateTableDefInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiFetch<MutationResult<AgentTableDefinition>>("/api/admin/agent-context/table-defs", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.tableDefs(tenantId) });
    },
  });
}

export interface UpdateTableDefInput {
  id: string;
  tableName?: string;
  description?: string;
  reason?: string;
}

export function useUpdateTableDef(
  tenantId: string | null,
): UseMutationResult<MutationResult<AgentTableDefinition>, Error, UpdateTableDefInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...rest }) =>
      apiFetch<MutationResult<AgentTableDefinition>>(
        `/api/admin/agent-context/table-defs/${encodeURIComponent(id)}`,
        { method: "PATCH", body: JSON.stringify(rest) },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.tableDefs(tenantId) });
      void qc.invalidateQueries({ queryKey: qk.tableDefRevisions(vars.id) });
    },
  });
}

export function useDeleteTableDef(
  tenantId: string | null,
): UseMutationResult<void, Error, DeleteEntityInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }) =>
      apiFetch<void>(`/api/admin/agent-context/table-defs/${encodeURIComponent(id)}`, {
        method: "DELETE",
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.tableDefs(tenantId) });
    },
  });
}

export function useTableDefRevisions(
  id: string | null,
): UseQueryResult<AgentTableDefinitionRevision[]> {
  return useQuery({
    queryKey: qk.tableDefRevisions(id ?? ""),
    queryFn: () =>
      apiFetch<AgentTableDefinitionRevision[]>(
        `/api/admin/agent-context/table-defs/${encodeURIComponent(id ?? "")}/revisions`,
      ),
    enabled: !!id,
  });
}

// ─── Field definitions ───────────────────────────────────────────────────────

export function useFieldDefs(tenantId: string | null): UseQueryResult<AgentFieldDefinition[]> {
  return useQuery({
    queryKey: qk.fieldDefs(tenantId),
    queryFn: () =>
      apiFetch<AgentFieldDefinition[]>(`/api/admin/agent-context/field-defs${listQS(tenantId)}`),
  });
}

export interface CreateFieldDefInput {
  tenantId: string | null;
  fieldName: string;
  body: FieldBody;
  tableName?: string;
  reason?: string;
}

export function useCreateFieldDef(
  tenantId: string | null,
): UseMutationResult<MutationResult<AgentFieldDefinition>, Error, CreateFieldDefInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiFetch<MutationResult<AgentFieldDefinition>>("/api/admin/agent-context/field-defs", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.fieldDefs(tenantId) });
    },
  });
}

export interface UpdateFieldDefInput {
  id: string;
  fieldName?: string;
  body?: FieldBody;
  tableName?: string;
  reason?: string;
}

export function useUpdateFieldDef(
  tenantId: string | null,
): UseMutationResult<MutationResult<AgentFieldDefinition>, Error, UpdateFieldDefInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...rest }) =>
      apiFetch<MutationResult<AgentFieldDefinition>>(
        `/api/admin/agent-context/field-defs/${encodeURIComponent(id)}`,
        { method: "PATCH", body: JSON.stringify(rest) },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.fieldDefs(tenantId) });
      void qc.invalidateQueries({ queryKey: qk.fieldDefRevisions(vars.id) });
    },
  });
}

export function useDeleteFieldDef(
  tenantId: string | null,
): UseMutationResult<void, Error, DeleteEntityInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }) =>
      apiFetch<void>(`/api/admin/agent-context/field-defs/${encodeURIComponent(id)}`, {
        method: "DELETE",
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.fieldDefs(tenantId) });
    },
  });
}

export function useFieldDefRevisions(
  id: string | null,
): UseQueryResult<AgentFieldDefinitionRevision[]> {
  return useQuery({
    queryKey: qk.fieldDefRevisions(id ?? ""),
    queryFn: () =>
      apiFetch<AgentFieldDefinitionRevision[]>(
        `/api/admin/agent-context/field-defs/${encodeURIComponent(id ?? "")}/revisions`,
      ),
    enabled: !!id,
  });
}

// ─── Tenant config (additional context) ──────────────────────────────────────

export function useTenantConfig(tenantId: string | null): UseQueryResult<AgentTenantConfig | null> {
  return useQuery({
    queryKey: qk.tenantConfig(tenantId ?? ""),
    queryFn: () =>
      apiFetch<AgentTenantConfig | null>(
        `/api/admin/agent-context/tenant-config/${encodeURIComponent(tenantId ?? "")}`,
      ),
    enabled: !!tenantId,
  });
}

export interface UpsertTenantConfigInput {
  tenantId: string;
  additionalContext: string | null;
  reason?: string;
}

export function useUpsertTenantConfig(
  tenantId: string | null,
): UseMutationResult<MutationResult<AgentTenantConfig>, Error, UpsertTenantConfigInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId: tId, ...rest }) =>
      apiFetch<MutationResult<AgentTenantConfig>>(
        `/api/admin/agent-context/tenant-config/${encodeURIComponent(tId)}`,
        { method: "PUT", body: JSON.stringify({ tenantId: tId, ...rest }) },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.tenantConfig(tenantId ?? "") });
      void qc.invalidateQueries({ queryKey: qk.tenantConfigRevisions(vars.tenantId) });
    },
  });
}

export function useTenantConfigRevisions(
  tenantId: string | null,
): UseQueryResult<AgentTenantConfigRevision[]> {
  return useQuery({
    queryKey: qk.tenantConfigRevisions(tenantId ?? ""),
    queryFn: () =>
      apiFetch<AgentTenantConfigRevision[]>(
        `/api/admin/agent-context/tenant-config/${encodeURIComponent(tenantId ?? "")}/revisions`,
      ),
    enabled: !!tenantId,
  });
}

// ─── Warehouse schema (T3 read-only panel) ───────────────────────────────────

/**
 * The tenant's BigQuery warehouse schema for the T3 panel. The route returns
 * 200 with `error: string | null` even on warehouse-side failures, so the
 * consumer surfaces `data.error` as an inline banner instead of taking the
 * QueryView error branch.
 */
export function useWarehouseSchema(tenantId: string | null): UseQueryResult<WarehouseSchema> {
  return useQuery({
    queryKey: qk.warehouseSchema(tenantId),
    queryFn: () =>
      apiFetch<WarehouseSchema>(
        `/api/admin/agent-context/warehouse-schema?tenantId=${encodeURIComponent(tenantId ?? "")}`,
      ),
    enabled: !!tenantId,
  });
}
